<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import Thumbnail from 'dashboard/components/widgets/Thumbnail.vue';
import { useAdmin } from 'dashboard/composables/useAdmin';
import SettingsLayout from '../SettingsLayout.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import {
  useMapGetter,
  useStoreGetters,
  useStore,
} from 'dashboard/composables/store';
import ChannelName from './components/ChannelName.vue';
import ChannelIcon from 'next/icon/ChannelIcon.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const CHANNEL_TYPE_TO_KEY = {
  'Channel::WebWidget': 'website',
  'Channel::Email': 'email',
  'Channel::Api': 'api',
  'Channel::Whatsapp': 'whatsapp',
  'Channel::Sms': 'sms',
  'Channel::Telegram': 'telegram',
  'Channel::Line': 'line',
  'Channel::FacebookPage': 'facebook',
  'Channel::Instagram': 'instagram',
  'Channel::Tiktok': 'tiktok',
  'Channel::Voice': 'voice',
};

const getters = useStoreGetters();
const store = useStore();
const { t } = useI18n();
const { isAdmin } = useAdmin();

const showDeletePopup = ref(false);
const selectedInbox = ref({});

const inboxes = useMapGetter('inboxes/getInboxes');
const accountId = useMapGetter('getCurrentAccountId');
const globalConfig = computed(() => getters['globalConfig/get'].value);

const inboxesList = computed(() =>
  inboxes.value
    ?.filter(i => i.channel_type !== 'Channel::FacebookPageComment')
    .slice()
    .sort((a, b) => a.name.localeCompare(b.name))
);

const channelCatalog = computed(() => {
  const apiName = globalConfig.value.apiChannelName || 'API';
  return [
    { key: 'website', name: 'Website' },
    { key: 'email', name: 'Email' },
    { key: 'api', name: apiName },
    { key: 'whatsapp', name: 'WhatsApp' },
    { key: 'sms', name: 'SMS' },
    { key: 'telegram', name: 'Telegram' },
    { key: 'line', name: 'LINE' },
    { key: 'facebook', name: 'Messenger' },
    { key: 'instagram', name: 'Instagram' },
    { key: 'tiktok', name: 'TikTok', incoming: true },
    { key: 'voice', name: 'Voice', incoming: true },
  ];
});

const connectedChannelKeys = computed(
  () =>
    new Set(
      inboxes.value
        .map(i => CHANNEL_TYPE_TO_KEY[i.channel_type])
        .filter(Boolean)
    )
);

const availableChannels = computed(() =>
  channelCatalog.value.filter(ch => !connectedChannelKeys.value.has(ch.key))
);

const uiFlags = computed(() => getters['inboxes/getUIFlags'].value);

const deleteConfirmText = computed(
  () => `${t('INBOX_MGMT.DELETE.CONFIRM.YES')} ${selectedInbox.value.name}`
);
const deleteRejectText = computed(
  () => `${t('INBOX_MGMT.DELETE.CONFIRM.NO')} ${selectedInbox.value.name}`
);
const confirmDeleteMessage = computed(
  () => `${t('INBOX_MGMT.DELETE.CONFIRM.MESSAGE')} ${selectedInbox.value.name}?`
);
const confirmPlaceHolderText = computed(
  () =>
    `${t('INBOX_MGMT.DELETE.CONFIRM.PLACE_HOLDER', {
      inboxName: selectedInbox.value.name,
    })}`
);

const deleteInbox = async ({ id }) => {
  try {
    await store.dispatch('inboxes/delete', id);
    useAlert(t('INBOX_MGMT.DELETE.API.SUCCESS_MESSAGE'));
  } catch (error) {
    useAlert(t('INBOX_MGMT.DELETE.API.ERROR_MESSAGE'));
  }
};
const closeDelete = () => {
  showDeletePopup.value = false;
  selectedInbox.value = {};
};
const confirmDeletion = () => {
  deleteInbox(selectedInbox.value);
  closeDelete();
};
const openDelete = inbox => {
  showDeletePopup.value = true;
  selectedInbox.value = inbox;
};
</script>

<template>
  <SettingsLayout :no-records-found="false" :is-loading="uiFlags.isFetching">
    <template #header>
      <BaseSettingsHeader
        :title="$t('INBOX_MGMT.HEADER')"
        :description="$t('INBOX_MGMT.DESCRIPTION')"
        :link-text="$t('INBOX_MGMT.LEARN_MORE')"
        feature-name="inboxes"
      >
        <template #actions>
          <router-link v-if="isAdmin" :to="{ name: 'settings_inbox_new' }">
            <Button
              icon="i-lucide-circle-plus"
              :label="$t('SETTINGS.INBOXES.NEW_INBOX')"
            />
          </router-link>
        </template>
      </BaseSettingsHeader>
    </template>

    <template #body>
      <div class="flex flex-col gap-8">
        <!-- Connected inboxes -->
        <section>
          <div class="flex items-center gap-2 mb-3">
            <h3
              class="text-xs font-semibold tracking-widest uppercase text-n-slate-11"
            >
              {{ $t('INBOX_MGMT.LIST.CONNECTED') }}
            </h3>
            <span
              class="px-1.5 text-[11px] font-medium rounded-full text-n-slate-11 bg-n-alpha-2"
            >
              {{ inboxesList.length }}
            </span>
          </div>

          <router-link
            v-if="!inboxesList.length && isAdmin"
            :to="{ name: 'settings_inbox_new' }"
            class="flex items-center gap-3 p-4 rounded-xl border border-dashed border-n-weak bg-n-solid-1 hover:border-n-strong hover:bg-n-alpha-1 transition-all duration-150"
          >
            <span class="i-lucide-circle-plus size-5 text-n-brand shrink-0" />
            <div>
              <p class="text-sm font-semibold text-n-slate-12">
                {{ $t('INBOX_MGMT.LIST.EMPTY_TITLE') }}
              </p>
              <p class="text-xs text-n-slate-11 mt-0.5">
                {{ $t('INBOX_MGMT.LIST.EMPTY_DESC') }}
              </p>
            </div>
          </router-link>
          <p v-else-if="!inboxesList.length" class="text-sm text-n-slate-11">
            {{ $t('INBOX_MGMT.LIST.404') }}
          </p>

          <div
            v-else
            class="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-5 gap-2.5"
          >
            <div
              v-for="inbox in inboxesList"
              :key="inbox.id"
              class="group relative flex flex-col items-center justify-center gap-2 aspect-square p-3 rounded-xl border border-n-weak bg-n-solid-1 hover:border-n-strong hover:shadow-sm transition-all duration-150"
            >
              <span
                class="absolute top-2 right-2 size-2 rounded-full bg-n-teal-10 ring-2 ring-n-solid-1 group-hover:opacity-0 transition-opacity duration-150"
              />

              <div
                v-if="isAdmin"
                class="absolute top-1.5 right-1.5 flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity duration-150"
              >
                <router-link
                  :to="{
                    name: 'settings_inbox_show',
                    params: { inboxId: inbox.id },
                  }"
                >
                  <Button
                    v-tooltip.top="$t('INBOX_MGMT.SETTINGS')"
                    icon="i-lucide-settings"
                    slate
                    xs
                    faded
                  />
                </router-link>
                <Button
                  v-tooltip.top="$t('INBOX_MGMT.DELETE.BUTTON_TEXT')"
                  icon="i-lucide-trash-2"
                  xs
                  ruby
                  faded
                  @click="openDelete(inbox)"
                />
              </div>

              <Thumbnail
                v-if="inbox.avatar_url"
                class="rounded-xl bg-n-alpha-3 shrink-0"
                :src="inbox.avatar_url"
                :username="inbox.name"
                size="48px"
              />
              <div
                v-else
                class="size-12 shrink-0 flex items-center justify-center rounded-xl bg-n-alpha-3 border border-n-weak"
              >
                <ChannelIcon class="size-6 text-n-slate-11" :inbox="inbox" />
              </div>

              <div class="flex flex-col items-center gap-0.5 min-w-0 w-full">
                <span
                  class="text-sm font-semibold text-n-slate-12 truncate text-center w-full leading-snug"
                >
                  {{ inbox.name }}
                </span>
                <span class="text-xs text-n-slate-11 truncate text-center">
                  <ChannelName
                    :channel-type="inbox.channel_type"
                    :medium="inbox.medium"
                  />
                </span>
              </div>
            </div>
          </div>
        </section>

        <!-- Available channels (admin only) -->
        <section v-if="isAdmin && availableChannels.length">
          <div class="flex items-center gap-2 mb-3">
            <h3
              class="text-xs font-semibold tracking-widest uppercase text-n-slate-11"
            >
              {{ $t('INBOX_MGMT.LIST.AVAILABLE') }}
            </h3>
            <span
              class="px-1.5 text-[11px] font-medium rounded-full text-n-slate-11 bg-n-alpha-2"
            >
              {{ availableChannels.length }}
            </span>
          </div>

          <div class="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-5 gap-2.5">
            <template v-for="ch in availableChannels" :key="ch.key">
              <!-- Active channel tile -->
              <router-link
                v-if="!ch.incoming"
                :to="{
                  name: 'settings_inboxes_page_channel',
                  params: { sub_page: ch.key, accountId },
                }"
                class="relative flex flex-col items-center justify-center gap-2 aspect-square p-3 rounded-xl border border-dashed border-n-weak bg-n-solid-1 hover:border-n-strong hover:bg-n-alpha-1 transition-all duration-150"
              >
                <div class="relative shrink-0">
                  <img
                    :src="`/assets/images/dashboard/channels/${ch.key}.png`"
                    :alt="ch.name"
                    draggable="false"
                    class="size-12 object-contain"
                  />
                  <span
                    class="absolute -bottom-1 -right-1 size-4 rounded-full bg-n-solid-1 border border-n-weak flex items-center justify-center"
                  >
                    <span class="i-lucide-plus size-2.5 text-n-slate-11" />
                  </span>
                </div>
                <div class="flex flex-col items-center gap-0.5 min-w-0 w-full">
                  <span
                    class="text-sm font-semibold text-n-slate-12 truncate text-center w-full leading-snug"
                  >
                    {{ ch.name }}
                  </span>
                  <span class="text-xs text-n-slate-10 text-center">
                    {{ $t('INBOX_MGMT.LIST.CONNECT') }}
                  </span>
                </div>
              </router-link>

              <!-- Incoming (disabled) channel tile -->
              <div
                v-else
                class="relative flex flex-col items-center justify-center gap-2 aspect-square p-3 rounded-xl border border-dashed border-n-weak bg-n-solid-1 opacity-50 cursor-not-allowed"
              >
                <img
                  :src="`/assets/images/dashboard/channels/${ch.key}.png`"
                  :alt="ch.name"
                  draggable="false"
                  class="size-12 object-contain grayscale"
                />
                <div class="flex flex-col items-center gap-0.5 min-w-0 w-full">
                  <span
                    class="text-sm font-semibold text-n-slate-12 truncate text-center w-full leading-snug"
                  >
                    {{ ch.name }}
                  </span>
                  <span class="text-xs text-n-slate-10 text-center">
                    {{ $t('INBOX_MGMT.LIST.INCOMING') }}
                  </span>
                </div>
              </div>
            </template>
          </div>
        </section>
      </div>

      <woot-confirm-delete-modal
        v-if="showDeletePopup"
        v-model:show="showDeletePopup"
        :title="$t('INBOX_MGMT.DELETE.CONFIRM.TITLE')"
        :message="confirmDeleteMessage"
        :confirm-text="deleteConfirmText"
        :reject-text="deleteRejectText"
        :confirm-value="selectedInbox.name"
        :confirm-place-holder-text="confirmPlaceHolderText"
        @on-confirm="confirmDeletion"
        @on-close="closeDelete"
      />
    </template>
  </SettingsLayout>
</template>
